import { useEffect, useState } from 'react';
import { Building2 } from '../../icons';
import { listResources, getReservationStatus, type ReservationResourceDto } from '../../api/reservation';
import { tCalendar } from '../../lib/calendar-i18n';
import { describeCalendarError, type FriendlyError } from './calendarErrors';
import { unavailableRoomIds, STATUS_RANGE_MAX_MS } from './room-availability';
import { formatAmPmTime as hm } from './time-label';

/**
 * 일정 추가 창의 회의실 — 옆 창(현황 표)으로 고르고, 창에는 «고른 방 한 줄 + 수정»만 둔다
 * (2026-10-19 사용자가 시안 「확정안」으로 고름). 고르는 표는 RoomStatusModal(docked) 그대로다.
 */

/** 회의실 목록 — 카드를 처음 켤 때 한 번 읽는다(RoomPicker 와 같은 길 · 같은 거르기). */
export function useMeetingRooms(enabled: boolean, categories: readonly string[]) {
  const [rooms, setRooms] = useState<ReservationResourceDto[] | null>(null);
  const [error, setError] = useState<FriendlyError | null>(null);
  useEffect(() => {
    if (!enabled || rooms !== null) return;
    let cancelled = false;
    listResources()
      .then((rows) => { if (!cancelled) setRooms(rows.filter((r) => r.isActive && categories.includes(r.category))); })
      .catch((err) => { if (!cancelled) { setRooms([]); setError(describeCalendarError(err, 'room_list')); } });
    return () => { cancelled = true; };
    // categories 는 모듈 상수(ROOM_CATEGORIES)다 — 넣으면 인라인 배열일 때 끝없이 부른다(RoomPicker 머리말과 같다).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, rooms]);
  return { rooms, error };
}

interface RoomChoiceSummaryProps {
  room: ReservationResourceDto;
  /** 이 일정의 시간 — 고른 방을 그 시간에 쓸 수 있나 미리 본다(최종 판정은 저장할 때 서버). */
  checkRange: { start: Date; end: Date } | null;
  category?: string;
  ignoreReservationId?: string | null;
  /** 이 일정 시간이 이미 지났다 — 예약 불가로 보인다. */
  past?: boolean;
  allDay?: boolean;
  disabled?: boolean;
  onEdit: () => void;
}

/** 고른 방 한 줄 — 이름 · 위치 · 시간 · 예약 가능 여부 + 「수정」. */
export function RoomChoiceSummary({ room, checkRange, category, ignoreReservationId, past = false, allDay = false, disabled = false, onEdit }: RoomChoiceSummaryProps) {
  const [busy, setBusy] = useState(false);
  const rangeStart = checkRange?.start.getTime() ?? NaN;
  const rangeEnd = checkRange?.end.getTime() ?? NaN;
  useEffect(() => {
    if (!(rangeStart < rangeEnd) || rangeEnd - rangeStart > STATUS_RANGE_MAX_MS) { setBusy(false); return; }
    let cancelled = false;
    const start = new Date(rangeStart);
    const end = new Date(rangeEnd);
    const t = setTimeout(() => {
      Promise.resolve()
        .then(() => getReservationStatus(start, end, category))
        .then((st) => { if (!cancelled) setBusy(unavailableRoomIds([room], st.reservations, start, end, ignoreReservationId).has(room.id)); })
        .catch(() => { if (!cancelled) setBusy(false); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [rangeStart, rangeEnd, room, category, ignoreReservationId]);

  const bad = past || busy;
  const meta = [room.location, room.requiresApproval ? tCalendar('calendar.room.needs_approval') : null].filter(Boolean).join(' · ');
  const when = checkRange && !allDay ? `${hm(checkRange.start)} – ${hm(checkRange.end)}` : '';
  return (
    <div className={`calendar-choice-summary${bad ? ' is-bad' : ''}`} data-testid="room-choice-summary">
      <Building2 size={16} aria-hidden className="calendar-choice-ico" />
      <span className="calendar-choice-body">
        <span className="calendar-choice-title">{room.name}{meta ? <span className="calendar-choice-meta"> · {meta}</span> : null}</span>
        <span className={`calendar-choice-sub${bad ? ' calendar-choice-sub--bad' : ' calendar-choice-sub--ok'}`}>
          {when ? `${when} · ` : ''}
          {tCalendar(bad ? 'calendar.room.summary_busy' : 'calendar.room.summary_ok')}
        </span>
      </span>
      <button type="button" className="calendar-choice-edit" onClick={onEdit} disabled={disabled}>
        {tCalendar('calendar.choice.edit')}
      </button>
    </div>
  );
}
